import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Alert, Card } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { Notice } from '../../financialPositionMeta';

type NoticeCardProps = {
    notice: Notice;
};

/**
 * A balance-sheet section with nothing in it and nothing to press.
 *
 * These sections fill themselves as a side effect of ordinary work — the first
 * stock receipt, the first connected account — so the card says where that work
 * happens rather than offering a button that would only navigate away.
 *
 * The status distinction is load-bearing: an empty inventory is a gap (grey),
 * an empty loan book is a clean balance sheet (green). Rendering both the same
 * way would tell a healthy company it is missing something.
 */
export const NoticeCard: React.FC<NoticeCardProps> = ({ notice }) => {
    const { t } = useTranslation('pages');

    return (
        <Card {...getNodeTestIdProp(`financial-position-notice-${notice.id}`)}>
            <Card.Header>
                <Card.Title className="flex items-center gap-2">
                    <Icon
                        aria-hidden
                        className="text-muted"
                        icon={notice.icon}
                    />
                    {t(notice.titleKey)}
                </Card.Title>
                <Card.Description>{t(notice.questionKey)}</Card.Description>
            </Card.Header>
            <Card.Content>
                <Alert status={notice.status}>
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Description>
                            {t(notice.noticeKey)}
                        </Alert.Description>
                    </Alert.Content>
                </Alert>
            </Card.Content>
        </Card>
    );
};
